<template>
  <v-table class="data-table" :class="classes">
    <thead v-if="headers && !hideHeader">
      <tr>
        <th v-if="skillName" class="text-left"></th>
        <th
          v-for="header in headers"
          :key="header"
          :class="textAlign || 'text-center'"
          class="grade-header"
        >
          {{ header }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="item in items" :key="getItemKey(item)">
        <td v-if="skillName">
          <div
            class="skill-name font-weight-bold"
            v-html="getItemTitle(item)"
          ></div>
          <div
            v-if="item.subtitle"
            class="skill-subtitle"
            v-html="item.subtitle"
          ></div>
        </td>
        <td
          v-for="header in headers"
          :key="header"
          :class="[item.alignments?.[header] || textAlign || 'text-center']"
          v-html="getItemValue(item, header)"
        ></td>
      </tr>
    </tbody>
  </v-table>
</template>

<script setup lang="ts">
  interface TableItem {
    [key: string]: any;
    subtitle?: string;
    alignments?: { [key: string]: string };
  }

  interface Props {
    headers?: string[];
    items: TableItem[];
    classes?: string;
    textAlign?: string;
    skillName?: boolean;
    setFontSize?: string;
    solidBg?: boolean;
    hideHeader?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    skillName: false,
    setFontSize: undefined,
    headers: undefined,
    solidBg: false,
    hideHeader: false,
  });

  function getItemKey(item: TableItem): string {
    return item.skill || item.title || item.id || JSON.stringify(item);
  }

  function getItemTitle(item: TableItem): string {
    return item.skill || item.title || '';
  }

  function getItemValue(item: TableItem, header: string): any {
    const key = header.toLowerCase();
    if (key === 'k') return item.k;
    if (key === '1') return item.grade1;
    if (key === '2') return item.grade2;
    return item[key];
  }
</script>

<style lang="scss" scoped>
  .data-table {
    thead th {
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
      font-weight: bold !important;
      color: rgb(var(--v-theme-tonal-black));
      font-size: v-bind('setFontSize || "18px"');
      padding-top: 12px;
      padding-bottom: 12px;
      background-color: white;
    }

    thead th:first-child {
      padding: v-bind('skillName ? "0" : "12px 16px"');
    }

    tbody {
      tr {
        background-color: white;

        &:nth-child(even) {
          background-color: v-bind('solidBg ? "white" : "#f0f3f5"');
        }

        td {
          border-bottom: 1px solid
            rgba(var(--v-border-color), var(--v-border-opacity));
          vertical-align: middle;
          padding: 16px !important;
          font-size: v-bind('setFontSize || "18px"');
          color: rgb(var(--v-theme-text-secondary));
        }

        &:last-child td {
          border-bottom: none;
        }

        td:first-child {
          padding-left: 0;
          padding-right: 16px;
        }
      }
    }

    .skill-name {
      color: rgb(var(--v-theme-text-primary));
      font-size: v-bind('setFontSize || "18px"');
      margin-bottom: 2px;
    }

    .skill-subtitle {
      color: rgb(var(--v-theme-text-secondary));
      font-size: v-bind('setFontSize ? `calc(${setFontSize} - 4px)` : "14px"');
    }

    .grade-header {
      padding-right: 16px;
    }
  }

  // Media query for mobile screens (xs and sm)
  @media (max-width: 959px) {
    .data-table {
      thead th,
      tbody td,
      .skill-name {
        font-size: 14px !important;
      }

      .skill-subtitle {
        font-size: 12px !important;
      }
    }
  }
</style>
